import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Drawer,
  Group,
  Modal,
  MultiSelect,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  NumberInput,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '@/api/admin';
import { errorMessage } from '@/api/http';
import { qk, useFormatGroups, useTaxonomy, useTermList } from '@/api/queries';
import {
  VOCABULARIES,
  type FormatGroup,
  type ImageRef,
  type TaxonomyTerm,
  type Vocabulary,
} from '@/api/types';
import { useCan } from '@/auth/useCan';
import { EmptyState } from '@/components/EmptyState';
import { ImageSlot } from '@/components/ImageSlot';
import { ListSearch } from '@/components/ListSearch';
import { ListFooter } from '@/components/PagedList';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { imageUrl } from '@/lib/image';
import { useListUrlState } from '@/lib/useListUrlState';
import { VOCAB_LABEL } from '@/lib/vocab';

/** Vocabularies whose terms can have a picture (Explore's topic pills). */
const HAS_IMAGE: Vocabulary[] = ['interest', 'subject'];

const VOCAB_HELP: Record<Vocabulary, string> = {
  interest:
    'Child-facing interests (“Animals”). Each maps to the subjects that count as that interest.',
  subject: 'Catalogue topics. Interests and parent objectives connect through subjects.',
  format:
    'Animation, live action, documentary… A programme can have one from each group. Children’s search matches any chosen format within a group and every group.',
  style: 'How it feels: gentle, funny…',
  energy: 'One per programme: calm, lively…',
  educational_value: 'What a child might learn or practise.',
  genre: 'Adventure, documentary…',
  parent_objective: 'Goals grown-ups can choose for their child.',
};

export function TaxonomyPage() {
  const { vocabulary } = useParams();
  const navigate = useNavigate();
  const vocab: Vocabulary = VOCABULARIES.includes(vocabulary as Vocabulary)
    ? (vocabulary as Vocabulary)
    : 'interest';
  const canEdit = useCan('editTaxonomy');
  const [editing, setEditing] = useState<TaxonomyTerm | 'new' | null>(null);
  const url = useListUrlState();
  const showArchived = url.get('archived') === 'true';

  return (
    <>
      <PageHeader
        title="Taxonomy"
        description="The vocabularies used to classify programmes and explain recommendations."
        actions={
          canEdit && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              New {VOCAB_LABEL[vocab].toLowerCase().replace(/s$/, '')}
            </Button>
          )
        }
      />
      {!canEdit && (
        <Alert color="sky" variant="light" mb="md">
          Vocabulary changes need the approver role. You can browse the terms.
        </Alert>
      )}
      <Tabs
        value={vocab}
        onChange={(v) => navigate(`/taxonomy/${v}`, { replace: true })}
        keepMounted={false}
      >
        <ScrollArea type="auto" scrollbarSize={6}>
          <Tabs.List mb="md" style={{ flexWrap: 'nowrap' }}>
            {VOCABULARIES.map((v) => (
              <Tabs.Tab key={v} value={v}>
                {VOCAB_LABEL[v]}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </ScrollArea>
        <Text size="sm" c="dimmed" mb="sm">
          {VOCAB_HELP[vocab]}
        </Text>
        <Group mb="md" gap="md">
          <ListSearch
            label={`Search ${VOCAB_LABEL[vocab].toLowerCase()}`}
            placeholder="Search labels"
            value={url.q}
            onSearch={url.setQ}
          />
          <Switch
            label="Show archived"
            checked={showArchived}
            onChange={(e) => url.setFilter('archived', e.currentTarget.checked ? 'true' : null)}
          />
        </Group>
        {vocab === 'format' && <FormatGroupsPanel canEdit={canEdit} />}
        <TermTable vocab={vocab} q={url.q} includeArchived={showArchived} onEdit={setEditing} />
      </Tabs>
      <Drawer
        opened={!!editing}
        onClose={() => setEditing(null)}
        position="right"
        size="md"
        title={editing === 'new' ? `New ${VOCAB_LABEL[vocab].toLowerCase()}` : `Edit term`}
      >
        {editing && (
          <TermForm
            vocab={vocab}
            term={editing === 'new' ? null : editing}
            canEdit={canEdit}
            onDone={() => setEditing(null)}
          />
        )}
      </Drawer>
    </>
  );
}

function TermTable({
  vocab,
  q,
  includeArchived,
  onEdit,
}: {
  vocab: Vocabulary;
  q: string | undefined;
  includeArchived: boolean;
  onEdit: (t: TaxonomyTerm) => void;
}) {
  const query = useTermList({ vocabulary: vocab, q, archived: includeArchived || undefined });
  // Labels for the interest → subject mapping: every subject, not just one page.
  const subjects = useTaxonomy('subject', true);
  const subjectLabel = (id: string) => subjects.data?.find((s) => s.id === id)?.label ?? '…';
  const groups = useFormatGroups();
  const groupLabel = (slug: string) => groups.data?.find((g) => g.slug === slug)?.label ?? slug;
  return (
    <QueryState query={query}>
      {({ items: terms }) =>
        terms.length === 0 ? (
          <EmptyState
            title={
              q
                ? `No ${VOCAB_LABEL[vocab].toLowerCase()} match`
                : `No ${VOCAB_LABEL[vocab].toLowerCase()} yet`
            }
          />
        ) : (
          <>
            <Table.ScrollContainer minWidth={600}>
              <Table striped style={{ opacity: query.isPlaceholderData ? 0.6 : 1 }}>
                <Table.Thead>
                  <Table.Tr>
                    {HAS_IMAGE.includes(vocab) && (
                      <Table.Th w={56}>
                        <VisuallyHidden>Picture</VisuallyHidden>
                      </Table.Th>
                    )}
                    <Table.Th>Label</Table.Th>
                    <Table.Th>Slug</Table.Th>
                    {vocab === 'interest' && <Table.Th>Maps to subjects</Table.Th>}
                    {vocab === 'interest' && <Table.Th>Child label</Table.Th>}
                    {vocab === 'parent_objective' && <Table.Th>Grown-up label</Table.Th>}
                    {vocab === 'format' && <Table.Th>Group</Table.Th>}
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {terms.map((t) => (
                    <Table.Tr key={t.id}>
                      {HAS_IMAGE.includes(vocab) && (
                        <Table.Td>
                          <TermPicture image={t.image} label={t.label} />
                        </Table.Td>
                      )}
                      <Table.Td>
                        <Anchor
                          component="button"
                          type="button"
                          fw={600}
                          c="inherit"
                          onClick={() => onEdit(t)}
                        >
                          {t.label}
                        </Anchor>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {t.slug}
                        </Text>
                      </Table.Td>
                      {vocab === 'interest' && (
                        <Table.Td>
                          {(t.subject_ids ?? []).length === 0 ? (
                            <Text size="sm" c="coral.8">
                              None — add subjects
                            </Text>
                          ) : (
                            <Group gap={4}>
                              {(t.subject_ids ?? []).map((id) => (
                                <Badge
                                  key={id}
                                  variant="light"
                                  color="leaf"
                                  style={{ textTransform: 'none' }}
                                >
                                  {subjectLabel(id)}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Table.Td>
                      )}
                      {vocab === 'interest' && <Table.Td>{t.child_label ?? '—'}</Table.Td>}
                      {vocab === 'parent_objective' && <Table.Td>{t.parent_label ?? '—'}</Table.Td>}
                      {vocab === 'format' && (
                        <Table.Td>
                          {t.format_group ? (
                            groupLabel(t.format_group)
                          ) : (
                            <Text size="sm" c="dimmed">
                              No group
                            </Text>
                          )}
                        </Table.Td>
                      )}
                      <Table.Td>
                        {t.archived ? (
                          <Badge color="gray">Archived</Badge>
                        ) : (
                          <Badge color="leaf">Active</Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <ListFooter query={query} noun="terms" />
          </>
        )
      }
    </QueryState>
  );
}

function TermForm({
  vocab,
  term,
  canEdit,
  onDone,
}: {
  vocab: Vocabulary;
  term: TaxonomyTerm | null;
  canEdit: boolean;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const subjects = useTaxonomy('subject');
  const groups = useFormatGroups();
  const form = useForm({
    initialValues: {
      label: term?.label ?? '',
      slug: term?.slug ?? '',
      child_label: term?.child_label ?? '',
      parent_label: term?.parent_label ?? '',
      onboarding_eligible: term?.onboarding_eligible ?? false,
      onboarding_min_age: (term?.onboarding_min_age ?? '') as number | '',
      onboarding_max_age: (term?.onboarding_max_age ?? '') as number | '',
      question_eligible: term?.question_eligible ?? false,
      subject_ids: term?.subject_ids ?? [],
      image: (term?.image ?? null) as ImageRef | null,
      format_group: term?.format_group ?? '',
    },
    validate: { label: (v) => (v.trim() ? null : 'Add a label.') },
  });
  const finish = (message: string) => {
    void qc.invalidateQueries({ queryKey: ['taxonomy'] });
    notifications.show({ color: 'leaf', message });
    onDone();
  };
  const save = useMutation({
    mutationFn: async (v: typeof form.values) => {
      const body = {
        label: v.label.trim(),
        slug: v.slug.trim() || undefined,
        child_label: v.child_label.trim() || null,
        parent_label: v.parent_label.trim() || null,
        question_eligible: v.question_eligible,
        ...(vocab === 'interest'
          ? {
              onboarding_eligible: v.onboarding_eligible,
              onboarding_min_age: v.onboarding_min_age === '' ? null : v.onboarding_min_age,
              onboarding_max_age: v.onboarding_max_age === '' ? null : v.onboarding_max_age,
            }
          : {}),
        ...(HAS_IMAGE.includes(vocab) ? { image: v.image } : {}),
        ...(vocab === 'format' ? { format_group: v.format_group || null } : {}),
      };
      const saved = term
        ? await adminApi.updateTerm(term.id, body)
        : await adminApi.createTerm({ ...body, vocabulary: vocab });
      if (
        vocab === 'interest' &&
        JSON.stringify([...v.subject_ids].sort()) !==
          JSON.stringify([...(term?.subject_ids ?? [])].sort())
      )
        await adminApi.setInterestSubjects(saved.id, v.subject_ids);
      return saved;
    },
    onSuccess: () => finish(term ? 'Term saved.' : 'Term added.'),
  });
  const archive = useMutation({
    mutationFn: () => adminApi.archiveTerm(term!.id),
    onSuccess: () => finish('Term archived.'),
    onError: (e) => notifications.show({ color: 'coral', message: errorMessage(e) }),
  });

  return (
    <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0 }}>
        <Stack>
          <TextInput label="Label" required data-autofocus {...form.getInputProps('label')} />
          <TextInput
            label="Slug"
            description="Stable key used by the app. Leave empty to generate."
            {...form.getInputProps('slug')}
          />
          {vocab === 'interest' && (
            <TextInput
              label="Child label"
              description="Short, read aloud to pre-readers."
              {...form.getInputProps('child_label')}
            />
          )}
          {vocab === 'parent_objective' && (
            <TextInput
              label="Grown-up label"
              description="How the goal is phrased to parents."
              {...form.getInputProps('parent_label')}
            />
          )}
          {HAS_IMAGE.includes(vocab) && (
            <ImageSlot
              kind="term_image"
              label="Picture"
              hint="Shown on the topic pills in children’s Explore. Square, at least 480 × 480."
              ratio={1}
              previewWidth={160}
              value={form.values.image}
              onChange={(v) => form.setFieldValue('image', v)}
              disabled={!canEdit}
            />
          )}
          {vocab === 'format' && (
            <Select
              label="Format group"
              description="Children choose any format within a group; a format with no group is matched on its own."
              placeholder="No group"
              clearable
              data={(groups.data ?? []).map((g) => ({ value: g.slug, label: g.label }))}
              {...form.getInputProps('format_group')}
            />
          )}
          <Switch
            label="Can be asked about in progressive questions"
            {...form.getInputProps('question_eligible', { type: 'checkbox' })}
          />
          {vocab === 'interest' && (
            <>
              <MultiSelect
                label="Subjects"
                description="Programmes with these subjects count as this interest (C8). Objectives connect to interests through subjects."
                searchable
                data={(subjects.data ?? []).map((s) => ({ value: s.id, label: s.label }))}
                {...form.getInputProps('subject_ids')}
              />
              <Switch
                label="Offered in onboarding"
                {...form.getInputProps('onboarding_eligible', { type: 'checkbox' })}
              />
              <Group grow>
                <NumberInput
                  label="Onboarding from age"
                  min={0}
                  max={18}
                  {...form.getInputProps('onboarding_min_age')}
                />
                <NumberInput
                  label="Onboarding to age"
                  min={0}
                  max={18}
                  {...form.getInputProps('onboarding_max_age')}
                />
              </Group>
            </>
          )}
          {save.error && (
            <Text size="sm" c="coral.8" role="alert">
              {errorMessage(save.error)}
            </Text>
          )}
          {canEdit && (
            <Group justify="space-between">
              {term && !term.archived ? (
                <Button
                  variant="subtle"
                  color="coral"
                  onClick={() =>
                    modals.openConfirmModal({
                      title: `Archive “${term.label}”?`,
                      children: (
                        <Text size="sm">
                          It can’t be chosen for new programmes. Existing programmes keep it until
                          edited.
                        </Text>
                      ),
                      labels: { confirm: 'Archive', cancel: 'Cancel' },
                      confirmProps: { color: 'coral' },
                      onConfirm: () => archive.mutate(),
                    })
                  }
                >
                  Archive
                </Button>
              ) : (
                <span />
              )}
              <Group gap="xs">
                <Button variant="default" onClick={onDone}>
                  Cancel
                </Button>
                <Button type="submit" loading={save.isPending}>
                  Save
                </Button>
              </Group>
            </Group>
          )}
        </Stack>
      </fieldset>
    </form>
  );
}

function TermPicture({ image, label }: { image: ImageRef | null | undefined; label: string }) {
  const src = imageUrl(image, 80, 80);
  return src ? (
    <img
      src={src}
      alt={`${label} picture`}
      width={40}
      height={40}
      style={{ borderRadius: 8, objectFit: 'cover', display: 'block' }}
    />
  ) : (
    <Text size="xs" c="dimmed" aria-label={`${label} has no picture`}>
      —
    </Text>
  );
}

/** Format groups (pb4 Explore): label, child label and order; approver+ to change. */
function FormatGroupsPanel({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const query = useFormatGroups();
  const [editing, setEditing] = useState<FormatGroup | 'new' | null>(null);
  const remove = useMutation({
    mutationFn: (slug: string) => adminApi.deleteFormatGroup(slug),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['taxonomy'] });
      notifications.show({ color: 'leaf', message: 'Format group deleted.' });
    },
    onError: (e) => notifications.show({ color: 'coral', message: errorMessage(e) }),
  });
  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Group justify="space-between" mb="xs">
        <div>
          <Text fw={600}>Format groups</Text>
          <Text size="sm" c="dimmed">
            How formats are grouped on children’s Explore, in this order.
          </Text>
        </div>
        {canEdit && (
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={() => setEditing('new')}
          >
            New group
          </Button>
        )}
      </Group>
      <QueryState query={query}>
        {(groups) =>
          groups.length === 0 ? (
            <Text size="sm" c="dimmed">
              No format groups yet. Each format is matched on its own.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={480}>
              <Table aria-label="Format groups">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Label</Table.Th>
                    <Table.Th>Child label</Table.Th>
                    <Table.Th>Slug</Table.Th>
                    <Table.Th>Formats</Table.Th>
                    {canEdit && (
                      <Table.Th>
                        <VisuallyHidden>Actions</VisuallyHidden>
                      </Table.Th>
                    )}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {groups.map((g) => (
                    <Table.Tr key={g.slug}>
                      <Table.Td fw={600}>{g.label}</Table.Td>
                      <Table.Td>{g.child_label ?? '—'}</Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {g.slug}
                        </Text>
                      </Table.Td>
                      <Table.Td>{g.term_count}</Table.Td>
                      {canEdit && (
                        <Table.Td>
                          <Group gap={4} justify="flex-end" wrap="nowrap">
                            <Button
                              variant="subtle"
                              size="compact-sm"
                              leftSection={<IconPencil size={14} />}
                              aria-label={`Edit ${g.label}`}
                              onClick={() => setEditing(g)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="subtle"
                              color="coral"
                              size="compact-sm"
                              leftSection={<IconTrash size={14} />}
                              aria-label={`Delete ${g.label}`}
                              onClick={() =>
                                modals.openConfirmModal({
                                  title: `Delete “${g.label}”?`,
                                  children: (
                                    <Text size="sm">
                                      Its {g.term_count} formats stay, with no group: children’s
                                      search then matches each of them on its own.
                                    </Text>
                                  ),
                                  labels: { confirm: 'Delete', cancel: 'Cancel' },
                                  confirmProps: { color: 'coral' },
                                  onConfirm: () => remove.mutate(g.slug),
                                })
                              }
                            >
                              Delete
                            </Button>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )
        }
      </QueryState>
      <Modal
        opened={!!editing}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New format group' : 'Edit format group'}
      >
        {editing && (
          <FormatGroupForm
            group={editing === 'new' ? null : editing}
            onDone={() => setEditing(null)}
          />
        )}
      </Modal>
    </Paper>
  );
}

function FormatGroupForm({ group, onDone }: { group: FormatGroup | null; onDone: () => void }) {
  const qc = useQueryClient();
  const form = useForm({
    initialValues: {
      label: group?.label ?? '',
      child_label: group?.child_label ?? '',
      slug: group?.slug ?? '',
      sort: (group?.sort ?? '') as number | '',
    },
    validate: { label: (v) => (v.trim() ? null : 'Add a label.') },
  });
  const save = useMutation({
    mutationFn: (v: typeof form.values) => {
      const body = {
        label: v.label.trim(),
        slug: v.slug.trim() || undefined,
        child_label: v.child_label.trim() || null,
        sort: v.sort === '' ? undefined : v.sort,
      };
      return group
        ? adminApi.updateFormatGroup(group.slug, body)
        : adminApi.createFormatGroup(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.formatGroups });
      void qc.invalidateQueries({ queryKey: ['taxonomy'] });
      notifications.show({
        color: 'leaf',
        message: group ? 'Format group saved.' : 'Format group added.',
      });
      onDone();
    },
  });
  return (
    <form onSubmit={form.onSubmit((v) => save.mutate(v))}>
      <Stack>
        <TextInput label="Label" required data-autofocus {...form.getInputProps('label')} />
        <TextInput
          label="Child label"
          description="Optional wording for children; the label is used otherwise."
          {...form.getInputProps('child_label')}
        />
        <TextInput
          label="Slug"
          description="Stable key used by the app (on_screen). Leave empty to generate. Changing it moves its formats with it."
          {...form.getInputProps('slug')}
        />
        <NumberInput
          label="Order"
          description="Lower comes first. Leave empty to add at the end."
          {...form.getInputProps('sort')}
        />
        {save.error && (
          <Text size="sm" c="coral.8" role="alert">
            {errorMessage(save.error)}
          </Text>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onDone}>
            Cancel
          </Button>
          <Button type="submit" loading={save.isPending}>
            Save
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
